import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';

interface ConsentModalProps {
  open: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ConsentModal: React.FC<ConsentModalProps> = ({ open, onConfirm, onCancel, isSubmitting }) => {
  const [agreed, setAgreed] = useState(false);

  const handleClose = () => {
    if (!isSubmitting) {
      setAgreed(false);
      onCancel();
    }
  };

  const handleConfirm = async () => {
    await onConfirm();
    setAgreed(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--panel-bg, #1e1e2e)',
          color: 'var(--text-primary, #e0e0e0)',
          borderRadius: '12px'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1 }}>
        Submit Professional Profile
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          By submitting your profile, you confirm that the information is accurate and
          grant SSI Strategy permission to use it in the course of client interactions.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={isSubmitting}
              sx={{
                color: 'var(--stone-grey, #888)',
                '&.Mui-checked': { color: 'var(--accent-blue, #4a9eff)' }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              I approve the use of the submitted profile for the promotion of my skills
              and experience in the course of SSI's client interactions.
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ color: 'var(--stone-grey, #888)' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!agreed || isSubmitting}
          variant="contained"
          sx={{
            bgcolor: 'var(--accent-blue, #4a9eff)',
            '&:hover': { bgcolor: 'var(--accent-blue-hover, #3a8eef)' },
            '&.Mui-disabled': { bgcolor: 'rgba(74, 158, 255, 0.3)' }
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
              Submitting...
            </>
          ) : (
            'Confirm and Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentModal;
